import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "../../icons";
import { Storefront as StorefrontIcon } from "../../icons";
import { LocationOn as LocationOnIcon } from "../../icons";
import { AccessTime as AccessTimeIcon } from "../../icons";
import { useNavigate, useLocation } from "react-router-dom";
import firebase from "firebase/compat/app";
import { pageContentSx } from "../../pageLayout";
import { auth, db, functions } from "../../firebase";
import { geocodeAddress, reverseGeocodeAddress } from "../../utils/geo";
import LocationPicker from "../../components/LocationPicker";
import StoresAppBar from "./StoresAppBar";
import PaypalButton from "../../components/PaypalButton";
import { T } from "../Translate";
import { brandColors } from "../../theme";

const STORE_CREATION_FEE = 5;
const STORE_CREATION_CURRENCY = "USD";

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const CreateStore = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preset = location.state || {};
  const [storeName, setStoreName] = useState(preset.storeName || "");
  const [description, setDescription] = useState(preset.description || "");
  const [address, setAddress] = useState(preset.address || "");
  const [hours, setHours] = useState(preset.hours || "");
  const [contactEmail, setContactEmail] = useState(preset.contactEmail || "");
  const [whatsapp, setWhatsapp] = useState(preset.whatsapp || "");
  const [paypalEmail, setPaypalEmail] = useState(preset.paypalEmail || "");
  const paypalClientId = preset.paypalClientId || "";
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [paymentCaptured, setPaymentCaptured] = useState(null);
  const [coords, setCoords] = useState(
    typeof preset.latitude === "number" && typeof preset.longitude === "number"
      ? { latitude: preset.latitude, longitude: preset.longitude }
      : null
  );
  const [locating, setLocating] = useState(false);
  const lastPickRef = useRef(0);

  // El usuario marcó un punto en el mapa: se guarda esa posición exacta y
  // se completa la dirección con la calle correspondiente.
  const handlePickLocation = async (latitude, longitude) => {
    setCoords({ latitude, longitude });
    const pickId = ++lastPickRef.current;
    setLocating(true);
    const found = await reverseGeocodeAddress(latitude, longitude);
    if (pickId !== lastPickRef.current) return;
    setLocating(false);
    if (found) setAddress(found);
  };

  // Ubica en el mapa la dirección escrita en el campo.
  const handleSearchAddress = async () => {
    const cleanAddress = address.trim();
    if (!cleanAddress) {
      setErrors((prev) => ({ ...prev, address: <T>Ingresa la dirección de tu tienda.</T> }));
      return;
    }
    lastPickRef.current++;
    setLocating(true);
    const found = await geocodeAddress(cleanAddress);
    setLocating(false);
    if (found) {
      setCoords(found);
      setErrors((prev) => ({ ...prev, address: undefined }));
    } else {
      setErrors((prev) => ({ ...prev, address: <T>No encontramos esa dirección. Marcá la ubicación en el mapa.</T> }));
    }
  };

  const previewSlug = useMemo(() => slugify(storeName) || "tu-tienda", [storeName]);

  const saveStore = async ({ slug, cleanedFields, user, payment }) => {
    const {
      cleanName,
      cleanDescription,
      cleanAddress,
      cleanHours,
      cleanPaypalEmail,
      cleanPaypalClientId,
      cleanContactEmail,
      cleanWhatsapp,
      pickedCoords,
    } = cleanedFields;

    setSaving(true);
    setFeedback(null);

    try {
      const docRef = db.collection("stores").doc(slug);
      const docSnapshot = await docRef.get();

      if (payment && docSnapshot.exists && docSnapshot.data()?.ownerId !== user.uid) {
        setFeedback({
          type: "error",
          message: (
            <T>Ese nombre de tienda se ocupó mientras pagabas. Contáctanos con tu ID de transacción de PayPal para resolverlo.</T>
          ),
        });
        return;
      }

      if (!payment && docSnapshot.exists && docSnapshot.data()?.ownerId !== user.uid) {
        setErrors({ storeName: <T>Este nombre ya está en uso. Prueba con otro.</T> });
        return;
      }

      // Ubicación marcada en el mapa; si no hay, se geocodifica la dirección.
      const location = pickedCoords || (await geocodeAddress(cleanAddress));

      const now = firebase.firestore.FieldValue.serverTimestamp();
      const payload = {
        storeName: cleanName,
        name: cleanName,
        description: cleanDescription,
        address: cleanAddress,
        hours: cleanHours,
        slug,
        ownerId: user.uid,
        ownerEmail: user.email || "",
        paypalEmail: cleanPaypalEmail,
        paypalClientId: cleanPaypalClientId,
        contactEmail: cleanContactEmail,
        whatsapp: cleanWhatsapp,
        updatedAt: now,
      };

      if (location) {
        payload.latitude = location.latitude;
        payload.longitude = location.longitude;
      }

      if (!docSnapshot.exists) {
        payload.createdAt = now;
      }

      if (payment) {
        // Tienda nueva: la crea la Cloud Function createPaidStore después de
        // verificar el pago con PayPal (las reglas no dejan crear stores/{slug}
        // desde el cliente). También guarda el pago y la notificación.
        await functions.httpsCallable("createPaidStore")({
          slug,
          orderId: payment.paypalTransactionId,
          fields: {
            name: cleanName,
            description: cleanDescription,
            address: cleanAddress,
            hours: cleanHours,
            paypalEmail: cleanPaypalEmail,
            paypalClientId: cleanPaypalClientId,
            contactEmail: cleanContactEmail,
            whatsapp: cleanWhatsapp,
            latitude: location?.latitude,
            longitude: location?.longitude,
          },
        });
      } else {
        await docRef.set(payload, { merge: true });
      }

      setFeedback({ type: "success", message: <T>Tu tienda se guardó correctamente.</T> });
      setAwaitingPayment(false);
      setPendingSubmission(null);
      setPaymentCaptured(null);

      navigate(`/stores/${slug}`, {
        state: {
          storeName: cleanName,
          description: cleanDescription,
          address: cleanAddress,
          hours: cleanHours,
          paypalEmail: cleanPaypalEmail,
          paypalClientId: cleanPaypalClientId,
          contactEmail: cleanContactEmail,
          whatsapp: cleanWhatsapp,
        },
        replace: false,
      });
    } catch (error) {
      console.error("Error al crear la tienda:", error);
      if (payment && ["functions/already-exists", "functions/failed-precondition"].includes(error.code)) {
        // Motivo concreto de createPaidStore (nombre ocupado, pago inválido o ya usado).
        setFeedback({ type: "error", message: error.message });
      } else if (payment) {
        setFeedback({
          type: "error",
          message: (
            <T>Tu pago se procesó, pero no pudimos guardar tu tienda. Usa "Reintentar guardar" o contáctanos con tu ID de transacción de PayPal.</T>
          ),
        });
      } else {
        setFeedback({ type: "error", message: <T>No pudimos guardar tu tienda. Inténtalo más tarde.</T> });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleValidateAndProceed = async (event) => {
    event.preventDefault();

    const validationErrors = {};
    const cleanName = storeName.trim();
    const cleanDescription = description.trim();
    const cleanPaypalEmail = paypalEmail.trim();
    const cleanPaypalClientId = paypalClientId.trim();
    const cleanAddress = address.trim();
    const cleanHours = hours.trim();
    const slug = slugify(cleanName);

    if (!cleanName) {
      validationErrors.storeName = <T>Ingresa un nombre para tu tienda.</T>;
    } else if (!slug) {
      validationErrors.storeName = <T>Usa caracteres alfanuméricos para el nombre.</T>;
    }

    if (!cleanDescription) {
      validationErrors.description = <T>Describe brevemente tu tienda.</T>;
    }

    if (!cleanAddress) {
      validationErrors.address = <T>Ingresa la dirección de tu tienda.</T>;
    }

    if (cleanPaypalEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanPaypalEmail)) {
      validationErrors.paypalEmail = <T>Ingresa un correo válido de PayPal.</T>;
    }

    if (cleanPaypalClientId && cleanPaypalClientId.length < 20) {
      validationErrors.paypalClientId = <T>El Client ID debe ser el proporcionado por PayPal.</T>;
    }

    // Validar email de contacto
    const cleanContactEmail = contactEmail.trim();
    if (cleanContactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanContactEmail)) {
      validationErrors.contactEmail = <T>Ingresa un correo válido de contacto.</T>;
    }
    // Validar WhatsApp (solo números, mínimo 8 dígitos)
    const cleanWhatsapp = whatsapp.trim();
    if (cleanWhatsapp && !/^\+?\d{8,15}$/.test(cleanWhatsapp.replace(/\s|-/g, ""))) {
      validationErrors.whatsapp = <T>Ingresa un número de WhatsApp válido (ej: +5491122334455).</T>;
    }

    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setFeedback({ type: "error", message: <T>Necesitas iniciar sesión para crear tu tienda.</T> });
      return;
    }

    const cleanedFields = {
      cleanName,
      cleanDescription,
      cleanAddress,
      cleanHours,
      cleanPaypalEmail,
      cleanPaypalClientId,
      cleanContactEmail,
      cleanWhatsapp,
      pickedCoords: coords,
    };

    setSaving(true);
    setFeedback(null);

    try {
      const docRef = db.collection("stores").doc(slug);
      const docSnapshot = await docRef.get();

      if (docSnapshot.exists && docSnapshot.data()?.ownerId !== user.uid) {
        setErrors({ storeName: <T>Este nombre ya está en uso. Prueba con otro.</T> });
        return;
      }

      if (docSnapshot.exists) {
        // Edición de una tienda propia: no se cobra.
        await saveStore({ slug, cleanedFields, user, payment: null });
      } else {
        // Tienda nueva: se guarda recién después de aprobar el pago.
        setPendingSubmission({ slug, cleanedFields, user });
        setAwaitingPayment(true);
      }
    } catch (error) {
      console.error("Error al validar la tienda:", error);
      setFeedback({ type: "error", message: <T>No pudimos validar tu tienda. Inténtalo más tarde.</T> });
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentApproved = async (details) => {
    setPaymentCaptured(details);
    if (!pendingSubmission) return;
    await saveStore({
      ...pendingSubmission,
      payment: {
        paypalTransactionId: details.id,
        paidAmount: STORE_CREATION_FEE,
        paidCurrency: STORE_CREATION_CURRENCY,
      },
    });
  };

  const handlePaymentError = (err) => {
    console.error("Error en el pago de PayPal:", err);
    setFeedback({ type: "error", message: <T>El pago no pudo procesarse. Intenta nuevamente.</T> });
  };

  const handleRetrySave = () => {
    if (!pendingSubmission || !paymentCaptured) return;
    saveStore({
      ...pendingSubmission,
      payment: {
        paypalTransactionId: paymentCaptured.id,
        paidAmount: STORE_CREATION_FEE,
        paidCurrency: STORE_CREATION_CURRENCY,
      },
    });
  };

  const handleBackToEdit = () => {
    setAwaitingPayment(false);
    setPendingSubmission(null);
  };

  return (
    <Box sx={{ bgcolor: brandColors.backgroundDefault, minHeight: "100vh", pb: "80px" }}>
      <StoresAppBar title={<T>Crear tienda</T>} subtitle={<T>Asistente de tiendas</T>} maxWidth="md" />
      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, pt: { xs: 6, md: 10 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="overline" sx={{ letterSpacing: 2, color: brandColors.accent }}>
              <T>Asistente de tiendas</T>
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              <T>Crea tu tienda online</T>
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
              <T>Completa el nombre y la descripción. Generaremos una URL del tipo notiline.com/stores/nombre-de-tu-tienda.</T>
            </Typography>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper component="form" onSubmit={handleValidateAndProceed} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4, backgroundColor: brandColors.backgroundPaper, border: `1px solid ${brandColors.divider}` }}>
              <Stack spacing={3}>
                <TextField
                  label={<T>Nombre de la tienda</T>}
                  value={storeName}
                  onChange={(event) => setStoreName(event.target.value)}
                  error={Boolean(errors.storeName)}
                  helperText={errors.storeName}
                  disabled={awaitingPayment}
                  fullWidth
                />
                <TextField
                  label={<T>Descripción</T>}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  multiline
                  minRows={4}
                  error={Boolean(errors.description)}
                  helperText={errors.description}
                  disabled={awaitingPayment}
                  fullWidth
                />
                <TextField
                  label={<T>Dirección</T>}
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  error={Boolean(errors.address)}
                  helperText={errors.address || <T>Ej: Av. Corrientes 1234, Buenos Aires</T>}
                  disabled={awaitingPayment}
                  fullWidth
                  InputProps={{
                    startAdornment: <LocationOnIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                />
                <LocationPicker
                  latitude={coords?.latitude}
                  longitude={coords?.longitude}
                  onPick={handlePickLocation}
                  onSearchAddress={handleSearchAddress}
                  busy={locating}
                  disabled={awaitingPayment || saving}
                />
                <TextField
                  label={<T>Horarios de atención</T>}
                  value={hours}
                  onChange={(event) => setHours(event.target.value)}
                  error={Boolean(errors.hours)}
                  multiline
                  minRows={2}
                  helperText={errors.hours || <T>Ej: Lun-Vie 9:00-18:00, Sáb 9:00-13:00</T>}
                  disabled={awaitingPayment}
                  fullWidth
                  InputProps={{
                    startAdornment: <AccessTimeIcon sx={{ mr: 1, color: "text.secondary", alignSelf: "flex-start", mt: 0.5 }} />,
                  }}
                />
                <TextField
                  label={<T>Correo de PayPal</T>}
                  value={paypalEmail}
                  onChange={(event) => setPaypalEmail(event.target.value)}
                  error={Boolean(errors.paypalEmail)}
                  helperText={
                    errors.paypalEmail || <T>Recibirás los pagos en este correo de PayPal.</T>
                  }
                  disabled={awaitingPayment}
                  fullWidth
                />
                <TextField
                  label={<T>Email de contacto</T>}
                  value={contactEmail}
                  onChange={(event) => setContactEmail(event.target.value)}
                  error={Boolean(errors.contactEmail)}
                  helperText={errors.contactEmail || <T>Este email será visible para compradores después de la compra.</T>}
                  disabled={awaitingPayment}
                  fullWidth
                />
                <TextField
                  label={<T>WhatsApp para contacto</T>}
                  value={whatsapp}
                  onChange={(event) => setWhatsapp(event.target.value)}
                  error={Boolean(errors.whatsapp)}
                  helperText={errors.whatsapp || <T>Ejemplo: +5491122334455. Será visible para compradores después de la compra.</T>}
                  disabled={awaitingPayment}
                  fullWidth
                />

                {!awaitingPayment && (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Button type="submit" variant="contained" size="large" disabled={saving}>
                      {saving ? <CircularProgress size={22} color="inherit" /> : <T>Generar enlace</T>}
                    </Button>
                  </Stack>
                )}

                {awaitingPayment && !paymentCaptured && (
                  <Stack spacing={2}>
                    <Alert severity="info">
                      <T>Crear una tienda tiene un costo único de {STORE_CREATION_FEE} {STORE_CREATION_CURRENCY} vía PayPal.</T>
                    </Alert>
                    <PaypalButton
                      amount={STORE_CREATION_FEE}
                      currency={STORE_CREATION_CURRENCY}
                      description={`Creación de tienda: ${pendingSubmission?.cleanedFields?.cleanName || storeName}`}
                      clientId={process.env.REACT_APP_PAYPAL_CLIENT_ID}
                      payeeEmail={process.env.REACT_APP_PLATFORM_PAYPAL_EMAIL}
                      metadata={{ customId: pendingSubmission?.slug }}
                      disabled={saving}
                      onApprove={handlePaymentApproved}
                      onError={handlePaymentError}
                      style={{ layout: "vertical", color: "blue", shape: "rect" }}
                    />
                    <Button variant="text" onClick={handleBackToEdit} disabled={saving}>
                      <T>Volver a editar</T>
                    </Button>
                  </Stack>
                )}

                {paymentCaptured && (
                  <Stack spacing={2}>
                    <Alert severity="warning">
                      <T>Tu pago de {STORE_CREATION_FEE} {STORE_CREATION_CURRENCY} se procesó correctamente (ID de
                      transacción: {paymentCaptured.id}), pero no pudimos guardar tu tienda. Podés reintentar sin que
                      se te vuelva a cobrar, o contactarnos con ese ID de transacción.</T>
                    </Alert>
                    <Button variant="contained" onClick={handleRetrySave} disabled={saving}>
                      {saving ? <CircularProgress size={22} color="inherit" /> : <T>Reintentar guardar</T>}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 4, borderRadius: 4, height: "100%", backgroundColor: brandColors.backgroundPaper, border: `1px solid ${brandColors.divider}` }}>
              <Stack spacing={2}>
                <StorefrontIcon sx={{ color: brandColors.accent, fontSize: 48 }} />
                <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                  <T>Vista previa</T>
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {storeName || <T>Nombre de la tienda</T>}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {description || <T>Aquí aparecerá la descripción de tu tienda.</T>}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {address ? address : <span style={{ color: brandColors.textSecondary }}><T>Dirección no definida</T></span>}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary", mt: 0.2 }} />
                  <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "pre-line" }}>
                    {hours ? hours : <span style={{ color: brandColors.textSecondary }}><T>Horarios no definidos</T></span>}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <b><T>Email de contacto:</T></b> {contactEmail ? contactEmail : <span style={{ color: brandColors.textSecondary }}><T>No definido</T></span>}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <b><T>WhatsApp:</T></b> {whatsapp ? whatsapp : <span style={{ color: brandColors.textSecondary }}><T>No definido</T></span>}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  URL
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  notiline.com/stores/{previewSlug}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <T>Tu enlace se generará automáticamente al guardar.</T>
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={5000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {feedback && (
          <Alert onClose={() => setFeedback(null)} severity={feedback.type} sx={{ width: "100%" }}>
            {feedback.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default CreateStore;
